import { createApolloClient } from "../../providers/ApolloClientFactory";
import { userStore } from "../../store/user";
import { organizationStore } from "../../store/organization";
import { eventStore } from "../../store/event";
import {
  INSERT_ANALYTICS_EVENTS,
  INSERT_ANALYTICS_EVENTS_RAW_QUERY,
} from "./mutations";
import {
  AnalyticsRouteContext,
  EventType,
  TrackEventOptions,
} from "./types";

const SESSION_ID_KEY = "ron-analytics-session";
const SESSION_USER_KEY = "ron-analytics-session-user";
const FLUSH_INTERVAL_MS = 15000;
const FLUSH_THRESHOLD = 10;

interface QueuedEvent {
  event_type: EventType;
  path: string;
  route_id: string | null;
  route_label: string | null;
  user_id: string;
  organization_id: number | null;
  event_id: number | null;
  session_id: string;
  client_event_id: string;
  duration_ms: number | null;
  error_code: string | null;
  payload: Record<string, unknown>;
}

let queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let unloadHandlerInstalled = false;
let currentRoute: AnalyticsRouteContext = {
  path: typeof window !== "undefined" ? window.location.pathname : "/",
  route_id: null,
  route_label: null,
};

const newId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const ensureSession = (): string | null => {
  const user = userStore.getState().user;
  if (!user) return null;

  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  const storedUser = sessionStorage.getItem(SESSION_USER_KEY);
  if (stored && storedUser === user.id) return stored;

  const fresh = newId();
  sessionStorage.setItem(SESSION_ID_KEY, fresh);
  sessionStorage.setItem(SESSION_USER_KEY, user.id);
  return fresh;
};

export const resetSession = () => {
  sessionStorage.removeItem(SESSION_ID_KEY);
  sessionStorage.removeItem(SESSION_USER_KEY);
};

export const setCurrentRoute = (route: AnalyticsRouteContext) => {
  currentRoute = route;
};

const toOrgId = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const parsed = typeof raw === "number" ? raw : parseInt(raw.toString(), 10);
  if (!Number.isFinite(parsed)) return null;
  if (parsed === 999999999) return null;
  return parsed;
};

export const trackEvent = (
  event_type: EventType,
  opts: TrackEventOptions = {}
) => {
  const user = userStore.getState().user;
  if (!user) return;

  const sessionId = ensureSession();
  if (!sessionId) return;

  const route: AnalyticsRouteContext = {
    path: opts.route?.path ?? currentRoute.path,
    route_id: opts.route?.route_id ?? currentRoute.route_id,
    route_label: opts.route?.route_label ?? currentRoute.route_label,
  };

  queue.push({
    event_type,
    path: route.path,
    route_id: route.route_id,
    route_label: route.route_label,
    user_id: user.id,
    organization_id: toOrgId(organizationStore.getState().organizationId),
    event_id: eventStore.getState().eventId ?? null,
    session_id: sessionId,
    client_event_id: newId(),
    duration_ms: opts.duration_ms ?? null,
    error_code: opts.error_code ?? null,
    payload: opts.payload ?? {},
  });

  if (queue.length >= FLUSH_THRESHOLD) {
    void flush();
  }
};

const flush = async ({ beacon = false }: { beacon?: boolean } = {}) => {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];

  if (beacon) {
    const token = localStorage.getItem("ron-auth");
    const endpoint = (import.meta as any).env?.VITE_SERVICE_HASURA_ENDPOINT;
    if (!endpoint) return;
    try {
      await fetch(endpoint, {
        method: "POST",
        keepalive: true,
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: INSERT_ANALYTICS_EVENTS_RAW_QUERY,
          variables: { objects: batch },
        }),
      });
    } catch {
      // analytics must never block UX
    }
    return;
  }

  try {
    const client = createApolloClient();
    await client.mutate({
      mutation: INSERT_ANALYTICS_EVENTS,
      variables: { objects: batch },
    });
  } catch {
    // drop after one attempt; never block UX
  }
};

export const startFlushTimer = () => {
  if (flushTimer !== null) return;
  flushTimer = window.setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
};

export const installUnloadFlush = () => {
  if (unloadHandlerInstalled) return;
  unloadHandlerInstalled = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flush({ beacon: true });
    }
  });
  window.addEventListener("pagehide", () => {
    void flush({ beacon: true });
  });
};
